'use client';

import { Stack, useMediaQuery, useTheme } from '@mui/material';
import Badge from '@mui/material/Badge';
import Box from '@mui/material/Box';
import Drawer from '@mui/material/Drawer';
import IconButton from '@mui/material/IconButton';
import { useColorScheme } from '@mui/material/styles';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import { hasKeys, varAlpha } from 'minimal-shared/utils';
import { useCallback, useEffect } from 'react';

import { Iconify } from '@infor/ui-lib/src/components/iconify';
import { Scrollbar } from '@infor/ui-lib/src/components/scrollbar';

import { themeConfig } from 'theme/theme-config';
import { primaryColorPresets } from 'theme/with-settings/color-presets';

import { useSettingsContext } from '../context/use-settings-context';
import type { SettingsDrawerProps, SettingsState } from '../types';

import { BaseOption } from './base-option';
import { FontFamilyOptions, FontSizeOptions } from './font-options';
import { FullScreenButton } from './fullscreen-button';
import { NavColorOptions, NavLayoutOptions } from './nav-layout-option';
import { PresetsOptions } from './presets-options';
import { LargeBlock, SmallBlock } from './styles';

export function SettingsDrawer({
  sx,
  defaultSettings,
  appName = 'inforcfc',
}: SettingsDrawerProps) {
  const settings = useSettingsContext();
  const theme = useTheme();
  const { mode, setMode, systemMode } = useColorScheme();
  const isLargeScreen = useMediaQuery(theme.breakpoints.up('lg'));

  const navLayoutOptions = useCallback(() => {
    if (appName === 'inforcfc') {
      return ['vertical', 'mini', 'floating'];
    }

    return ['vertical', 'horizontal', 'mini'];
  }, [appName]);

  useEffect(() => {
    if (mode === 'system' && systemMode) {
      settings.setState({ colorScheme: systemMode });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mode, systemMode]);

  // Visible options by default settings
  const isFontFamilyVisible = hasKeys(defaultSettings, ['fontFamily']);
  const isCompactLayoutVisible = hasKeys(defaultSettings, ['compactLayout']);
  const isColorSchemeVisible = hasKeys(defaultSettings, ['colorScheme']);
  const isContrastVisible = hasKeys(defaultSettings, ['contrast']);
  const isNavColorVisible = hasKeys(defaultSettings, ['navColor']);
  const isNavLayoutVisible = hasKeys(defaultSettings, ['navLayout']);
  const isPrimaryColorVisible = hasKeys(defaultSettings, ['primaryColor']);
  const isFontSizeVisible = hasKeys(defaultSettings, ['fontSize']);

  const handleReset = useCallback(() => {
    settings.onReset();
    setMode(defaultSettings.colorScheme);
  }, [defaultSettings.colorScheme, setMode, settings]);

  const renderHead = () => (
    <Box
      sx={{
        py: 2,
        pr: 1,
        pl: 2.5,
        display: 'flex',
        alignItems: 'center',
      }}
    >
      <Typography variant="h6" sx={{ flexGrow: 1 }}>
        Configurações
      </Typography>

      <FullScreenButton />

      <Tooltip title="Restaurar Configurações">
        <IconButton onClick={handleReset}>
          <Badge color="error" variant="dot" invisible={!settings.canReset}>
            <Iconify icon="solar:restart-bold" />
          </Badge>
        </IconButton>
      </Tooltip>

      <Tooltip title="Fechar">
        <IconButton onClick={settings.onCloseDrawer}>
          <Iconify icon="mingcute:close-line" />
        </IconButton>
      </Tooltip>
    </Box>
  );

  const renderMode = () => (
    <BaseOption
      label="Modo Escuro"
      icon="moon"
      selected={settings.state.colorScheme === 'dark'}
      onChangeOption={() => {
        setMode(mode === 'light' ? 'dark' : 'light');
        settings.setState({ colorScheme: mode === 'light' ? 'dark' : 'light' });
      }}
    />
  );

  const renderContrast = () => (
    <BaseOption
      label="Contraste"
      icon="contrast"
      selected={settings.state.contrast === 'hight'}
      onChangeOption={() =>
        settings.setState({
          contrast: settings.state.contrast === 'default' ? 'hight' : 'default',
        })
      }
    />
  );

  const renderCompact = () => (
    <BaseOption
      tooltip="Disponível apenas em resoluções maiores que 1600px (xl)"
      label="Compacto"
      icon="autofit-width"
      selected={!!settings.state.compactLayout}
      onChangeOption={() =>
        settings.setState({ compactLayout: !settings.state.compactLayout })
      }
    />
  );

  const renderPresets = () => (
    <LargeBlock
      title="Cores"
      canReset={settings.state.primaryColor !== defaultSettings.primaryColor}
      onReset={() =>
        settings.setState({ primaryColor: defaultSettings.primaryColor })
      }
    >
      <PresetsOptions
        options={
          Object.keys(primaryColorPresets).map((key) => ({
            name: key,
            value: primaryColorPresets[key]?.main ?? '',
          })) as { name: SettingsState['primaryColor']; value: string }[]
        }
        value={settings.state.primaryColor}
        onChangeOption={(newOption) =>
          settings.setState({ primaryColor: newOption })
        }
      />
    </LargeBlock>
  );

  const renderNav = () => (
    <LargeBlock title="Nav" tooltip="Apenas para o dashboard" sx={{ gap: 2.5 }}>
      {isNavLayoutVisible && (
        <SmallBlock
          label="Layout"
          canReset={settings.state.navLayout !== defaultSettings.navLayout}
          onReset={() =>
            settings.setState({ navLayout: defaultSettings.navLayout })
          }
        >
          {isLargeScreen ? (
            <NavLayoutOptions
              options={
                navLayoutOptions() as (
                  | 'vertical'
                  | 'horizontal'
                  | 'mini'
                  | undefined
                )[]
              }
              value={settings.state.navLayout}
              onChangeOption={(newOption) =>
                settings.setState({ navLayout: newOption })
              }
            />
          ) : (
            <Stack
              sx={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
              }}
            >
              <Typography
                sx={{
                  color: 'text.secondary',
                  fontSize: 12,
                }}
              >
                Esta funcionalidade não está disponível em monitores com
                resoluções menores que 1200px
              </Typography>
            </Stack>
          )}
        </SmallBlock>
      )}
      {isNavColorVisible && (
        <SmallBlock
          label="Cor das Barras de Navegação"
          canReset={settings.state.navColor !== defaultSettings.navColor}
          onReset={() =>
            settings.setState({ navColor: defaultSettings.navColor })
          }
        >
          <NavColorOptions
            options={['integrate', 'apparent']}
            value={settings.state.navColor}
            onChangeOption={(newOption) =>
              settings.setState({ navColor: newOption })
            }
          />
        </SmallBlock>
      )}
    </LargeBlock>
  );

  const renderFont = () => (
    <LargeBlock title="Fonte" sx={{ gap: 2.5 }}>
      {isFontFamilyVisible && (
        <SmallBlock
          label="Tipo"
          canReset={settings.state.fontFamily !== defaultSettings.fontFamily}
          onReset={() =>
            settings.setState({ fontFamily: defaultSettings.fontFamily })
          }
        >
          <FontFamilyOptions
            options={[
              themeConfig.fontFamily.primary,
              'Inter Variable',
              'DM Sans Variable',
              'Nunito Sans Variable',
            ]}
            value={settings.state.fontFamily}
            onChangeOption={(newOption) =>
              settings.setState({ fontFamily: newOption })
            }
          />
        </SmallBlock>
      )}
      {isFontSizeVisible && (
        <SmallBlock
          label="Tamanho"
          canReset={settings.state.fontSize !== defaultSettings.fontSize}
          onReset={() =>
            settings.setState({ fontSize: defaultSettings.fontSize })
          }
          sx={{ gap: 5 }}
        >
          <FontSizeOptions
            options={[12, 20]}
            value={settings.state.fontSize}
            onChangeOption={(newOption) =>
              settings.setState({ fontSize: newOption })
            }
          />
        </SmallBlock>
      )}
    </LargeBlock>
  );

  return (
    <Drawer
      anchor="right"
      open={settings.openDrawer}
      onClose={settings.onCloseDrawer}
      slotProps={{
        backdrop: { invisible: true },
        paper: {
          sx: [
            (theme) => ({
              ...theme.mixins.paperStyles(theme, {
                color: varAlpha(
                  theme.vars.palette.background.defaultChannel,
                  0.9
                ),
              }),
              width: 360,
              zIndex: 10002,
            }),
            // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
            ...(Array.isArray(sx) ? sx : [sx]),
          ],
        },
      }}
      ModalProps={{
        container: () => document.body,
        style: { zIndex: 10002 },
      }}
    >
      {renderHead()}

      <Scrollbar>
        <Box
          sx={{
            pb: 5,
            gap: 6,
            px: 2.5,
            display: 'flex',
            flexDirection: 'column',
          }}
        >
          <Box
            sx={{
              gap: 2,
              display: 'grid',
              gridTemplateColumns: 'repeat(2, 1fr)',
            }}
          >
            {isColorSchemeVisible && renderMode()}
            {isContrastVisible && renderContrast()}
            {isCompactLayoutVisible && renderCompact()}
          </Box>

          {(isNavColorVisible || isNavLayoutVisible) && renderNav()}
          {isPrimaryColorVisible && renderPresets()}
          {(isFontFamilyVisible || isFontSizeVisible) && renderFont()}
        </Box>
      </Scrollbar>
    </Drawer>
  );
}
